import {
  CollageFamily,
  CollageTemplateId,
  GradientPresetId,
  getCollageTemplate,
  getGradientPreset,
} from '@/content/collageTemplates'
import { OutputPresetId } from '@/content/instagram'
import {
  CollageBackgroundKind,
  DEFAULT_OUTPUT_BACKGROUND,
  DOCUMENT_VERSION,
  NodeKind,
  MAX_COLLAGE_BACKGROUND_BLUR,
  MAX_COLLAGE_GAP,
  TransformKind,
  assertAdjustmentsParams,
  isIdentityAdjustments,
  validateOutputSettings,
  type AdjustmentsParams,
  type CompositionNode,
  type OrientationParams,
  type OutputSettings,
} from '@/core/document'
import type { ExportOptions } from '@/core/export/types'
import { validateExportOptions } from '@/core/export/validate'
import { assertOrientationParams, isIdentityOrientation } from '@/core/geometry'
import { InvalidRecentEditError, PersistenceUnavailableError } from '@/core/persistence/errors'
import type { PersistedSourceRecord, RecentEditRecord } from '@/core/persistence/types'
import type { EditSessionState } from '@/core/store/editSession'
import { FrameStyleId, assertHexColor, validateFrameParams, type FrameParams } from '@/tools/frame/params'

type UnknownRecord = Record<string, unknown>

function requireRecord(value: unknown, label: string): UnknownRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new InvalidRecentEditError(`${label} must be an object.`)
  }
  return value as UnknownRecord
}

function requireString(value: unknown, label: string): string {
  if (typeof value !== 'string' || !value.trim()) throw new InvalidRecentEditError(`${label} must be a non-empty string.`)
  return value
}

function requirePositiveInteger(value: unknown, label: string): number {
  if (!Number.isInteger(value) || (value as number) <= 0) {
    throw new InvalidRecentEditError(`${label} must be a positive integer.`)
  }
  return value as number
}

function requireTimestamp(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new InvalidRecentEditError(`${label} must be a non-negative timestamp.`)
  }
  return value
}

function requireFiniteNumber(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new InvalidRecentEditError(`${label} must be a finite number.`)
  }
  return value
}

function validatePersistedSource(value: unknown, index: number): PersistedSourceRecord {
  const source = requireRecord(value, `sources[${index}]`)
  if (!(source.file instanceof Blob)) throw new InvalidRecentEditError(`sources[${index}].file must be a Blob.`)
  return {
    id: requireString(source.id, `sources[${index}].id`) as PersistedSourceRecord['id'],
    fileName: requireString(source.fileName, `sources[${index}].fileName`),
    mimeType: requireString(source.mimeType, `sources[${index}].mimeType`),
    width: requirePositiveInteger(source.width, `sources[${index}].width`),
    height: requirePositiveInteger(source.height, `sources[${index}].height`),
    file: source.file,
  }
}

function validateImageNodeShape(
  node: UnknownRecord,
  label: string,
  sourceIds: ReadonlySet<string>,
): void {
  const sourceId = requireString(node.sourceId, `${label}.sourceId`)
  if (!sourceIds.has(sourceId)) throw new InvalidRecentEditError(`${label} source "${sourceId}" is missing.`)
  const transforms = requireRecord(node.transforms, `${label}.transforms`)
  for (const transform of Object.keys(transforms)) {
    if (!Object.values(TransformKind).includes(transform as TransformKind)) {
      throw new InvalidRecentEditError(`${label} transform "${transform}" is unknown.`)
    }
  }
  const orientationValue = transforms[TransformKind.Orientation]
  if (orientationValue !== undefined) {
    const orientation = requireRecord(orientationValue, `${label} orientation`)
    const params = {
      quarterTurns: orientation.quarterTurns,
      flipHorizontal: orientation.flipHorizontal,
      flipVertical: orientation.flipVertical,
      straightenDeg: orientation.straightenDeg,
    } as OrientationParams
    try {
      assertOrientationParams(params)
    } catch (error) {
      throw new InvalidRecentEditError(error instanceof Error ? error.message : 'document orientation is invalid.')
    }
    if (isIdentityOrientation(params)) {
      throw new InvalidRecentEditError('document identity orientation must be omitted.')
    }
  }
  const cropValue = transforms[TransformKind.Crop]
  if (cropValue !== undefined) {
    const crop = requireRecord(cropValue, `${label} crop`)
    const rect = requireRecord(crop.rect, `${label} crop rectangle`)
    requirePositiveInteger(rect.width, `${label} crop width`)
    requirePositiveInteger(rect.height, `${label} crop height`)
    if (!Number.isInteger(rect.x) || (rect.x as number) < 0 || !Number.isInteger(rect.y) || (rect.y as number) < 0) {
      throw new InvalidRecentEditError(`${label} crop origin must contain non-negative integers.`)
    }
  }
  const adjustmentsValue = transforms[TransformKind.Adjustments]
  if (adjustmentsValue !== undefined) {
    const adjustments = requireRecord(adjustmentsValue, `${label} adjustments`)
    const params: AdjustmentsParams = {
      brightness: requireFiniteNumber(adjustments.brightness, `${label} adjustment brightness`),
      contrast: requireFiniteNumber(adjustments.contrast, `${label} adjustment contrast`),
      saturation: requireFiniteNumber(adjustments.saturation, `${label} adjustment saturation`),
    }
    try {
      assertAdjustmentsParams(params)
    } catch (error) {
      throw new InvalidRecentEditError(error instanceof Error ? error.message : 'document adjustments are invalid.')
    }
    if (isIdentityAdjustments(params)) {
      throw new InvalidRecentEditError('document identity adjustments must be omitted.')
    }
  }
}

function validateNodeShape(
  value: unknown,
  label: string,
  sourceIds: ReadonlySet<string>,
  nodeIds: Set<string>,
): CompositionNode {
  const node = requireRecord(value, label)
  const id = requireString(node.id, `${label}.id`)
  if (nodeIds.has(id)) throw new InvalidRecentEditError(`document contains duplicate node id "${id}".`)
  nodeIds.add(id)
  switch (node.kind) {
    case NodeKind.Image:
      validateImageNodeShape(node, label, sourceIds)
      break
    case NodeKind.Frame: {
      const params = requireRecord(node.params, `${label}.params`)
      if (params.styleId === FrameStyleId.Polaroid) delete params.windowFill
      try {
        validateFrameParams(params as unknown as FrameParams)
      } catch (error) {
        throw new InvalidRecentEditError(error instanceof Error ? error.message : `${label} frame parameters are invalid.`)
      }
      validateNodeShape(node.child, `${label}.child`, sourceIds, nodeIds)
      break
    }
    case NodeKind.Collage: {
      if (!Object.values(CollageTemplateId).includes(node.templateId as CollageTemplateId)) {
        throw new InvalidRecentEditError(`${label} template "${String(node.templateId)}" is unknown.`)
      }
      const template = getCollageTemplate(node.templateId as CollageTemplateId)
      const gap = requireFiniteNumber(node.gap, `${label}.gap`)
      if (gap < 0 || gap > MAX_COLLAGE_GAP) {
        throw new InvalidRecentEditError(`${label}.gap must be from 0 to ${MAX_COLLAGE_GAP}.`)
      }
      if (template.family === CollageFamily.Prints && gap !== 0) {
        throw new InvalidRecentEditError(`${label}.gap must be 0 for Prints templates.`)
      }
      const background = requireRecord(node.background, `${label}.background`)
      try {
        switch (background.kind) {
          case CollageBackgroundKind.Color:
            assertHexColor(requireString(background.color, `${label}.background.color`), 'Collage background color')
            break
          case CollageBackgroundKind.Gradient: {
            if (!Object.values(GradientPresetId).includes(background.presetId as GradientPresetId)) {
              throw new Error(`Unknown gradient preset: "${String(background.presetId)}"`)
            }
            getGradientPreset(background.presetId as GradientPresetId)
            if (!Array.isArray(background.colors) || background.colors.length !== 2) {
              throw new Error('Collage gradient colors must contain exactly two colors')
            }
            assertHexColor(requireString(background.colors[0], `${label}.background.colors[0]`), 'Collage gradient start color')
            assertHexColor(requireString(background.colors[1], `${label}.background.colors[1]`), 'Collage gradient end color')
            break
          }
          case CollageBackgroundKind.Image: {
            const sourceId = requireString(background.sourceId, `${label}.background.sourceId`)
            if (!sourceIds.has(sourceId)) {
              throw new Error(`Collage background source "${sourceId}" is missing`)
            }
            const blur = requireFiniteNumber(background.blur, `${label}.background.blur`)
            if (blur < 0 || blur > MAX_COLLAGE_BACKGROUND_BLUR) {
              throw new Error(`Collage background blur must be from 0 to ${MAX_COLLAGE_BACKGROUND_BLUR} px`)
            }
            break
          }
          default:
            throw new Error(`Unknown collage background kind: "${String(background.kind)}"`)
        }
      } catch (error) {
        throw new InvalidRecentEditError(error instanceof Error ? error.message : `${label} background is invalid.`)
      }
      if (!Array.isArray(node.cells) || node.cells.length !== template.cells.length) {
        throw new InvalidRecentEditError(`${label}.cells must contain exactly ${template.cells.length} entries.`)
      }
      node.cells.forEach((child, index) => {
        if (child !== null) validateNodeShape(child, `${label}.cells[${index}]`, sourceIds, nodeIds)
      })
      if (!Array.isArray(node.tray)) throw new InvalidRecentEditError(`${label}.tray must be an array.`)
      node.tray.forEach((child, index) => validateNodeShape(child, `${label}.tray[${index}]`, sourceIds, nodeIds))
      break
    }
    default:
      throw new InvalidRecentEditError(`${label} kind "${String(node.kind)}" is unknown.`)
  }
  return node as unknown as CompositionNode
}

function validateDocumentShape(
  value: unknown,
  sourceIds: ReadonlySet<string>,
): { document: RecentEditRecord['document']; nodeIds: ReadonlySet<string> } {
  const document = requireRecord(value, 'document')
  if (document.version !== DOCUMENT_VERSION) {
    throw new InvalidRecentEditError(`document version must be ${DOCUMENT_VERSION}.`)
  }
  const output = requireRecord(document.output, 'document.output')
  if (!Object.values(OutputPresetId).includes(output.presetId as OutputPresetId)) {
    throw new InvalidRecentEditError(`document output preset "${String(output.presetId)}" is unknown.`)
  }
  if (output.split === undefined) output.split = null
  if (output.background === undefined) output.background = DEFAULT_OUTPUT_BACKGROUND
  try {
    validateOutputSettings(output as unknown as OutputSettings)
  } catch (error) {
    throw new InvalidRecentEditError(error instanceof Error ? error.message : 'document output settings are invalid.')
  }
  const nodeIds = new Set<string>()
  validateNodeShape(document.root, 'document.root', sourceIds, nodeIds)
  return { document: document as unknown as RecentEditRecord['document'], nodeIds }
}

export function assertRecentEditRecord(value: unknown): asserts value is RecentEditRecord {
  const record = requireRecord(value, 'record')
  requireString(record.id, 'record.id')
  requireTimestamp(record.createdAt, 'record.createdAt')
  requireTimestamp(record.updatedAt, 'record.updatedAt')
  if (typeof record.isOpen !== 'boolean') throw new InvalidRecentEditError('record.isOpen must be a boolean.')
  if (!Array.isArray(record.sources) || record.sources.length === 0) {
    throw new InvalidRecentEditError('record.sources must contain at least one source.')
  }
  const sources = record.sources.map(validatePersistedSource)
  const sourceIds = new Set(sources.map((source) => source.id))
  if (sourceIds.size !== sources.length) throw new InvalidRecentEditError('record.sources contains duplicate ids.')
  if (!(record.thumbnail instanceof Blob)) throw new InvalidRecentEditError('record.thumbnail must be a Blob.')
  const { nodeIds } = validateDocumentShape(record.document, sourceIds)
  if (record.selectedNodeId !== null
    && (typeof record.selectedNodeId !== 'string' || !nodeIds.has(record.selectedNodeId))) {
    throw new InvalidRecentEditError('record.selectedNodeId does not reference a document node.')
  }
  const options = requireRecord(record.exportOptions, 'record.exportOptions')
  if (options.customLongestSide === undefined) options.customLongestSide = null
  try {
    validateExportOptions(options as unknown as ExportOptions)
  } catch (error) {
    throw new InvalidRecentEditError(error instanceof Error ? error.message : 'Export options are invalid.')
  }
}

export function recordFromState(
  state: EditSessionState,
  thumbnail: Blob,
  existing?: RecentEditRecord,
): RecentEditRecord {
  if (!state.document) throw new Error('Cannot serialize an edit without a document')
  if (!(thumbnail instanceof Blob)) throw new Error('Cannot serialize an edit without a thumbnail Blob')
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
    throw new PersistenceUnavailableError('This browser cannot create stable recent-edit ids.')
  }
  const now = Date.now()
  const sources = [...state.sources.entries()].map(([id, source]): PersistedSourceRecord => ({
    id,
    fileName: source.fileName,
    mimeType: source.mimeType,
    width: source.width,
    height: source.height,
    file: source.file,
  }))
  const record: RecentEditRecord = {
    id: existing?.id ?? crypto.randomUUID(),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    isOpen: existing?.isOpen ?? true,
    sources,
    thumbnail,
    document: state.document,
    selectedNodeId: state.selectedNodeId,
    exportOptions: { ...state.exportOptions },
  }
  assertRecentEditRecord(record)
  return record
}
